import type { Payment } from "../../../domain/entities/Payment";
import type { PaymentTransaction } from "../../../domain/entities/PaymentTransaction";
import type { PaymentProcessorPort } from "../../../application/ports/PaymentProcessorPort";
import type { PaymentVoucherPort } from "../../../application/ports/PaymentVoucherPort";
import type { PaymentTransactionHttp } from "../../mappers/paymentTransactionMapper";
import { mapPaymentTransaction } from "../../mappers/paymentTransactionMapper";

interface PaymentsProcessorHttpAdapterConfig {
  baseUrl: string;
  authorizationToken: string;
}

export class PaymentsProcessorHttpAdapter implements PaymentProcessorPort, PaymentVoucherPort {
  constructor(private readonly config: PaymentsProcessorHttpAdapterConfig) {}

  async process(payment: Payment): Promise<PaymentTransaction> {
    const url = this.composeUrl(`pay/${encodeURIComponent(payment.method.code)}`);

    const response = await fetch(url, {
      method: "POST",
      headers: this.buildJsonHeaders(),
      body: JSON.stringify(this.buildPaymentBody(payment)),
    });

    if (!response.ok) {
      throw new Error("No fue posible procesar el pago en este momento");
    }

    const payload = (await response.json()) as PaymentTransactionHttp;
    return mapPaymentTransaction(payload);
  }

  async upload(params: { paymentMethodCode: string; transactionCode: string; file: File; email: string }): Promise<void> {
    const url = this.composeUrl(
      `pay/${encodeURIComponent(params.paymentMethodCode)}/${encodeURIComponent(params.transactionCode)}/uploadVoucher`,
    );

    const formData = new FormData();
    formData.append("photo", params.file);
    formData.append("email", params.email);

    const response = await fetch(url, {
      method: "POST",
      headers: this.buildMultipartHeaders(),
      body: formData,
    });

    if (!response.ok) {
      throw new Error("No fue posible cargar el comprobante de pago");
    }
  }

  private buildPaymentBody(payment: Payment): Record<string, string | number> {
    const baseBody: Record<string, string | number> = {
      order_code: payment.orderId,
      payment_method: payment.method.code,
      token: payment.token,
      currency: payment.currency,
    };

    const cleanedMetadata = Object.entries(payment.metadata ?? {}).reduce<Record<string, string | number>>(
      (acc, [key, value]) => {
        if (typeof value === "number") {
          if (Number.isFinite(value)) {
            acc[key] = value;
          }
          return acc;
        }

        const trimmed = value.trim();

        if (trimmed.length > 0) {
          acc[key] = trimmed;
        }

        return acc;
      },
      {},
    );

    return {
      ...baseBody,
      ...cleanedMetadata,
    };
  }

  private composeUrl(path: string): string {
    const normalizedBase = this.config.baseUrl.endsWith("/") ? this.config.baseUrl : `${this.config.baseUrl}/`;
    return new URL(path, normalizedBase).toString();
  }

  private buildJsonHeaders(): HeadersInit {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `${this.config.authorizationToken}`,
    };
  }

  private buildMultipartHeaders(): HeadersInit {
    return {
      Authorization: `${this.config.authorizationToken}`,
    };
  }
}
